import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CHAT_MESSAGE_MAX, PosTerminalChatService, SupportChatMessage, SupportChatState } from '../pos-terminal-chat.service';
import { PosTerminalSupportService } from '../pos-terminal-support.service';

// The support chat over every window, without an overlay: the app stays usable while it is open.
// Minimized it is a bubble with the unread count. It takes the bottom-left corner when the support
// badge is in the bottom-right one.
@Component({
  selector: 'app-support-chat',
  standalone: true,
  imports: [CommonModule, IonicModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="view$ | async as view">
      <ng-container *ngIf="view.state.status !== 'none' && !view.state.hidden">
        <button
          *ngIf="view.state.minimized"
          type="button"
          class="chat-bubble"
          [class.chat--left]="view.left"
          title="Чат підтримки"
          (click)="chat.expand()"
        >
          <ion-icon name="chatbubbles"></ion-icon>
          <span class="chat-bubble__count" *ngIf="view.state.unread">{{ view.state.unread }}</span>
        </button>

        <section *ngIf="!view.state.minimized" class="chat" [class.chat--left]="view.left" aria-label="Чат підтримки">
          <header class="chat__head">
            <span class="chat__dot" [class.chat__dot--off]="view.state.status !== 'open'"></span>
            <span class="chat__title">
              <b>Підтримка</b>
              <small>{{ view.state.status === 'open' ? view.state.operator || 'оператор' : 'чат завершено' }}</small>
            </span>
            <button *ngIf="view.state.status === 'open'" type="button" class="chat__icon" title="Згорнути" (click)="chat.minimize()">
              <ion-icon name="remove"></ion-icon>
            </button>
            <button *ngIf="view.state.status !== 'open'" type="button" class="chat__icon" title="Закрити" (click)="chat.dismiss()">
              <ion-icon name="close"></ion-icon>
            </button>
          </header>

          <div class="chat__list" #list>
            <div
              *ngFor="let message of view.state.messages; trackBy: byId"
              class="chat-msg"
              [class.chat-msg--mine]="message.from === 'terminal'"
            >
              <div class="chat-msg__bubble">
                <small class="chat-msg__kind" *ngIf="message.kind === 'request'">Запит підтримки</small>
                <span>{{ message.text }}</span>
              </div>
              <small class="chat-msg__meta">
                <span *ngIf="message.author">{{ message.author }} · </span>{{ message.atUtc | date: 'HH:mm' }}
                <span *ngIf="message.status === 'sending'"> · надсилається…</span>
                <button *ngIf="message.status === 'failed'" type="button" class="chat-msg__retry" (click)="chat.retry(message.id)">
                  не надіслано · повторити
                </button>
              </small>
            </div>
            <div class="chat__note" *ngIf="!view.state.messages.length">Напишіть оператору</div>
            <div class="chat__note" *ngIf="view.state.status === 'closed'">Оператор завершив чат</div>
          </div>

          <form *ngIf="view.state.status === 'open'" class="chat__form" (submit)="$event.preventDefault(); send()">
            <textarea
              rows="2"
              placeholder="Повідомлення"
              [attr.maxlength]="max"
              [value]="text"
              (input)="onText($event)"
              (keydown.enter)="onEnter($event)"
            ></textarea>
            <button type="submit" class="chat__send" title="Надіслати" [disabled]="!text.trim()">
              <ion-icon name="send"></ion-icon>
            </button>
          </form>
        </section>
      </ng-container>
    </ng-container>
  `,
  styles: [
    `
      .chat,
      .chat-bubble {
        position: fixed;
        z-index: 2147482000;
        right: calc(12px + env(safe-area-inset-right, 0px));
        bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      }
      .chat--left {
        right: auto;
        left: calc(12px + env(safe-area-inset-left, 0px));
      }
      .chat-bubble {
        width: 56px;
        height: 56px;
        border: none;
        border-radius: 50%;
        background: var(--ion-color-primary, #3880ff);
        color: var(--ion-color-primary-contrast, #fff);
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);
        font-size: 26px;
      }
      .chat-bubble__count {
        position: absolute;
        top: -2px;
        right: -2px;
        min-width: 20px;
        height: 20px;
        padding: 0 5px;
        border-radius: 10px;
        background: var(--ion-color-danger, #eb445a);
        font-size: 12px;
        line-height: 20px;
      }
      .chat {
        display: flex;
        flex-direction: column;
        width: min(360px, calc(100vw - 24px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)));
        height: min(460px, calc(100vh - 24px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)));
        overflow: hidden;
        border-radius: 12px;
        background: var(--ion-background-color, #fff);
        color: var(--ion-text-color, #000);
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
        font-size: 14px;
      }
      .chat__head {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 6px 8px 12px;
        background: var(--ion-color-primary, #3880ff);
        color: var(--ion-color-primary-contrast, #fff);
      }
      .chat__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #69f0ae;
      }
      .chat__dot--off {
        background: rgba(255, 255, 255, 0.5);
      }
      .chat__title {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
        line-height: 1.2;
      }
      .chat__title small {
        overflow: hidden;
        opacity: 0.85;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .chat__icon {
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 50%;
        background: transparent;
        color: inherit;
        font-size: 20px;
      }
      .chat__list {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 8px;
        padding: 10px;
        overflow-y: auto;
        background: var(--ion-color-light, #f4f5f8);
      }
      .chat-msg {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        max-width: 85%;
      }
      .chat-msg--mine {
        align-self: flex-end;
        align-items: flex-end;
      }
      .chat-msg__bubble {
        display: flex;
        flex-direction: column;
        padding: 7px 10px;
        border-radius: 12px 12px 12px 4px;
        background: var(--ion-background-color, #fff);
        white-space: pre-wrap;
        word-break: break-word;
      }
      .chat-msg--mine .chat-msg__bubble {
        border-radius: 12px 12px 4px 12px;
        background: var(--ion-color-primary, #3880ff);
        color: var(--ion-color-primary-contrast, #fff);
      }
      .chat-msg__kind {
        opacity: 0.8;
        font-size: 11px;
      }
      .chat-msg__meta {
        margin-top: 2px;
        color: var(--ion-color-medium, #92949c);
        font-size: 11px;
      }
      .chat-msg__retry {
        padding: 0;
        border: none;
        background: none;
        color: var(--ion-color-danger, #eb445a);
        font-size: 11px;
        text-decoration: underline;
      }
      .chat__note {
        align-self: center;
        color: var(--ion-color-medium, #92949c);
        font-size: 12px;
      }
      .chat__form {
        display: flex;
        align-items: flex-end;
        gap: 6px;
        padding: 8px;
        border-top: 1px solid var(--ion-color-light-shade, #d7d8da);
      }
      .chat__form textarea {
        flex: 1;
        max-height: 96px;
        padding: 8px 10px;
        border: 1px solid var(--ion-color-light-shade, #d7d8da);
        border-radius: 8px;
        background: var(--ion-background-color, #fff);
        color: inherit;
        font: inherit;
        resize: none;
      }
      .chat__send {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 50%;
        background: var(--ion-color-primary, #3880ff);
        color: var(--ion-color-primary-contrast, #fff);
        font-size: 18px;
      }
      .chat__send:disabled {
        opacity: 0.4;
      }
    `,
  ],
})
export class SupportChatComponent implements OnInit, OnDestroy {
  @ViewChild('list') list?: ElementRef<HTMLElement>;

  readonly max = CHAT_MESSAGE_MAX;
  text = '';
  readonly view$: Observable<{ state: SupportChatState; left: boolean }>;

  private _shown = 0;
  private readonly _subscription = new Subscription();

  constructor(
    public chat: PosTerminalChatService,
    private _support: PosTerminalSupportService,
    private _cd: ChangeDetectorRef,
  ) {
    this.view$ = combineLatest([this.chat.state$, this._support.corner$]).pipe(
      map(([state, corner]) => ({ state, left: corner === 'bottom-right' })),
    );
  }

  ngOnInit(): void {
    // Scrolls to the newest message when one arrives or the chat is expanded.
    this._subscription.add(
      this.chat.state$.subscribe((state) => {
        const count = state.minimized ? -1 : state.messages.length;
        if (count !== this._shown) {
          this._shown = count;
          setTimeout(() => this.list?.nativeElement.scrollTo({ top: this.list.nativeElement.scrollHeight }));
        }
      }),
    );
  }

  onText(event: Event): void {
    this.text = (event.target as HTMLTextAreaElement).value;
  }

  // Enter sends, Shift+Enter starts a new line.
  onEnter(event: Event): void {
    if (!(event as KeyboardEvent).shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  send(): void {
    const text = this.text.trim();
    if (!text) {
      return;
    }

    this.text = '';
    this._cd.markForCheck();
    void this.chat.send(text);
  }

  byId(_: number, message: SupportChatMessage): string {
    return message.id;
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
